import { mixed, string } from 'yup';
import { useTranslation, useCompassDigitalID } from '../../composables';
import type { RecordEntityId } from '@centric-os/types';

/**
 * Provides reusable Yup validation schemas for different types of IDs.
 * Includes validation for RecordEntityId, UUID, and CompassDigitalId, with required and optional variants.
 *
 * @returns {{
 *   RecordEntityIdSchemaRequired: MixedSchema<RecordEntityId>;
 *   RecordEntityIdSchemaOptional: MixedSchema<RecordEntityId>;
 *   UUIDSchemaRequired: StringSchema;
 *   UUIDSchemaOptional: StringSchema;
 *   CompassDigitalIdSchemaRequired: StringSchema;
 *   CompassDigitalIdSchemaOptional: StringSchema;
 * }}
 */
export default () => {
  // Set localization
  const { translate } = useTranslation();

  // Set compass digital id composable
  const { valid: validId } = useCompassDigitalID();

  /**
   * Validation schema for a required RecordEntityId.
   * Ensures the value is nullable and must be provided.
   *
   * @type {MixedSchema<RecordEntityId>}
   */
  const RecordEntityIdSchemaRequired = mixed<RecordEntityId>()
    .nullable()
    .required(translate('errorMessages.required'));

  /**
   * Validation schema for an optional RecordEntityId.
   * Ensures the value is nullable and optional.
   *
   * @type {MixedSchema<RecordEntityId>}
   */
  const RecordEntityIdSchemaOptional = mixed<RecordEntityId>().nullable().optional();

  /**
   * Validation schema for a required UUID.
   * Ensures the value is a valid UUID, nullable, and must be provided.
   *
   * @type {StringSchema}
   */
  const UUIDSchemaRequired = string()
    .uuid(translate('errorMessages.invalidType', { type: 'UUID' }))
    .nullable()
    .required(translate('errorMessages.required'));

  /**
   * Validation schema for an optional UUID.
   * Ensures the value is a valid UUID, nullable, and optional.
   *
   * @type {StringSchema}
   */
  const UUIDSchemaOptional = string()
    .uuid(translate('errorMessages.invalidType', { type: 'UUID' }))
    .nullable()
    .optional();

  /**
   * Helper function to create a validation schema for CompassDigitalId.
   * Uses `CompassDigitalId.valid()` to validate if the ID is valid.
   *
   * @returns {StringSchema} - A validation schema for CompassDigitalId.
   */
  const createCompassDigitalIdValidation = () =>
    string().test(
      'valid-id',
      translate('errorMessages.invalidType', { type: 'ID' }),
      (value) => !value || validId(value), // Validate only if value exists
    );

  /**
   * Validation schema for a required CompassDigitalId.
   * Ensures the value is nullable, valid according to `CompassDigitalId.valid()`, and must be provided.
   *
   * @type {StringSchema}
   */
  const CompassDigitalIdSchemaRequired = createCompassDigitalIdValidation()
    .nullable()
    .required(translate('errorMessages.required'));

  /**
   * Validation schema for an optional CompassDigitalId.
   * Ensures the value is nullable, valid according to `CompassDigitalId.valid()`, and optional.
   *
   * @type {StringSchema}
   */
  const CompassDigitalIdSchemaOptional = createCompassDigitalIdValidation().nullable().optional();

  return {
    RecordEntityIdSchemaRequired,
    RecordEntityIdSchemaOptional,
    UUIDSchemaRequired,
    UUIDSchemaOptional,
    CompassDigitalIdSchemaRequired,
    CompassDigitalIdSchemaOptional,
  };
};
